import { createConfigParametersRepository } from '../../configParametersRepository';
import type {
  GenericStorageData,
  TrackingStorageClient,
} from '../../configParametersRepository/storageClient/types';
import { isFlowOrProductChanged } from '../../configParametersRepository/storageClient/utils';
import {
  isParameterValueChanged,
  removeKeysFromObject,
} from '../../utils/object';
import {
  getCustomParamsFromDimensions,
  getUserPropertiesFromDimensions,
} from '../google-analytics/ga4';
import type { CustomDimensionsOrParams } from '../google-analytics/ga4Types';
import { trackUserProperties } from '../google-analytics/userProperties';
import {
  CUSTOM_PARAMS_STORAGE_KEY,
  USER_PROPERTIES_STORAGE_KEY,
} from '../resources/constants';
import type { StorageTrackingParams } from '../types';
import {
  customMetrics,
  customParamsHitFlowScope,
  customParamsHitSearchScope,
  customParamsProduct,
} from './constants';

const cleanAndSetParams = <T extends object>(
  storageClient: TrackingStorageClient,
  keysToRemove: (keyof T)[],
  originalParams: T,
  storageKey: keyof StorageTrackingParams
) => {
  const cleanedParams: Partial<T> = removeKeysFromObject(
    keysToRemove,
    originalParams
  );
  storageClient.update(storageKey, cleanedParams as GenericStorageData);
};

/**
 * Cleans hit/product/search-scope custom params from the storage when the `searches`
 * value changes. No-op if `searches` is unchanged.
 */
export const cleanStorageFromSearchScopeDimensions = (
  searches: string | number
) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  if (!storageClient) return;

  const parametersInStorage = storageClient.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );

  if (!isParameterValueChanged('searches', searches, parametersInStorage)) {
    return;
  }

  const hitAndProductScopeParameters = [
    ...customParamsProduct,
    ...customMetrics,
    ...customParamsHitSearchScope,
  ];
  cleanAndSetParams(
    storageClient,
    hitAndProductScopeParameters,
    parametersInStorage,
    CUSTOM_PARAMS_STORAGE_KEY
  );
};

/**
 * Cleans hit/product/flow/search-scope custom params from the storage when the flow or
 * product in the incoming screen name has changed. No-op otherwise.
 */
export const cleanStorageFromFlowScopeDimensions = (screenName: string) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  if (!storageClient) return;

  const parametersInStorage = storageClient.getTrackingStoredValue(
    CUSTOM_PARAMS_STORAGE_KEY
  );

  if (!isFlowOrProductChanged(screenName, parametersInStorage)) {
    return;
  }

  const hitAndProductScopeParameters = [
    ...customParamsProduct,
    ...customMetrics,
    ...customParamsHitSearchScope,
    ...customParamsHitFlowScope,
  ];
  cleanAndSetParams(
    storageClient,
    hitAndProductScopeParameters,
    parametersInStorage,
    CUSTOM_PARAMS_STORAGE_KEY
  );
};

const filterDimensionsAndParams = (
  customDimensionsAndParams: CustomDimensionsOrParams
) => {
  const { searches, screen_name } = getCustomParamsFromDimensions(
    customDimensionsAndParams
  );
  if (!searches && !screen_name) {
    return;
  }
  if (searches) {
    cleanStorageFromSearchScopeDimensions(searches);
  }
  if (screen_name) {
    cleanStorageFromFlowScopeDimensions(screen_name);
  }
};

const updateCustomParams = (
  storageClient: TrackingStorageClient,
  customDimensionsAndParams: CustomDimensionsOrParams
) => {
  const customParams = getCustomParamsFromDimensions(customDimensionsAndParams);
  if (!customParams || !Object.keys(customParams).length) {
    return;
  }
  storageClient.update(CUSTOM_PARAMS_STORAGE_KEY, (existingData = {}) => ({
    ...existingData,
    ...customParams,
  }));
};

const updateUserProperties = (
  storageClient: TrackingStorageClient,
  customDimensionsAndParams: CustomDimensionsOrParams
) => {
  const userProps = getUserPropertiesFromDimensions(customDimensionsAndParams);
  if (!userProps || !Object.keys(userProps).length) {
    return;
  }
  trackUserProperties(userProps);
  storageClient.update(USER_PROPERTIES_STORAGE_KEY, (existingData = {}) => ({
    ...existingData,
    ...userProps,
  }));
};

/**
 * Orchestrates persistence and side-effects when custom dimensions/params change:
 * - cleans scope-bound params if `searches` or `screen_name` changed
 * - persists incoming custom params
 * - persists user properties and forwards them to GA4 via `trackUserProperties`
 *
 * This logic lives in the `third-party-tracking` layer to keep
 * `configParametersRepository/storageClient` free of GA4 knowledge.
 */
export const updateCustomParamsAndDimensions = (
  customDimensionsAndParams: CustomDimensionsOrParams
) => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  const storageClient = getTrackingStorageClient();
  if (!storageClient) return;

  filterDimensionsAndParams(customDimensionsAndParams);
  updateCustomParams(storageClient, customDimensionsAndParams);
  updateUserProperties(storageClient, customDimensionsAndParams);
};
